
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  MenuItem,
  Typography,
  FormControl,
  Select,
  Card,
  CardContent,
  Avatar,
  CircularProgress,
  Link,
  Chip,
  Autocomplete,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import DeleteIcon from "@mui/icons-material/Delete";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from "react-toastify";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "../../api";

// ─── Shared styles ────────────────────────────────────────────────────────────

const gradientBg = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";

const gradientButtonSx = {
  background: gradientBg,
  color: "white",
  fontWeight: 600,
  px: 4,
  py: 1.5,
  borderRadius: 2,
  textTransform: "none",
  boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
  "&:hover": {
    background: "linear-gradient(135deg, #5a6fd8, #6a42a0)",
    transform: "translateY(-2px)",
    boxShadow: "0 8px 25px rgba(102, 126, 234, 0.4)",
  },
  "&:disabled": { opacity: 0.6, transform: "none" },
  transition: "all 0.25s ease",
};

const fieldBoxSx = {
  background: "linear-gradient(135deg, #f8f9ff 0%, #e8f4fd 100%)",
  border: "1px solid rgba(102, 126, 234, 0.12)",
  borderRadius: 2,
  p: 2.5,
};

const selectSx = {
  borderRadius: 1.5,
  backgroundColor: "white",
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
};

const autocompleteSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    backgroundColor: "white",
    boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
    "& fieldset": { border: "none" },
  },
};

// ─── Section header ───────────────────────────────────────────────────────────

const SectionHeader = ({ number, label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
    <Avatar
      sx={{
        width: 28,
        height: 28,
        fontSize: 13,
        fontWeight: 700,
        background: gradientBg,
        flexShrink: 0,
      }}
    >
      {number}
    </Avatar>
    <Typography variant="subtitle1" fontWeight={600} color="#1a1a2e">
      {label}
    </Typography>
    <Box sx={{ flex: 1, height: "1px", bgcolor: "rgba(102,126,234,0.15)" }} />
  </Box>
);

// ─── Yup schema ───────────────────────────────────────────────────────────────

const fileTests = (schema) =>
  schema
    .test("fileTypes", "Some files have unsupported formats", (files) => {
      if (!files || files.length === 0) return true;
      const ok = ["video/mp4", "video/mov", "video/avi", "video/wmv", "application/pdf", "image/jpg", "image/jpeg", "image/png"];
      return files.every((f) => ok.includes(f.type));
    })
    .test("fileSizes", "Some files are too large (max 50 MB each)", (files) => {
      if (!files) return true;
      return files.every((f) => f.size <= 50 * 1024 * 1024);
    })
    .test("maxFiles", "Maximum 10 files allowed", (files) => {
      if (!files) return true;
      return files.length <= 10;
    });

const createSchema = (isEditMode) =>
  yup.object().shape({
    courseId: yup.string().required("Course is required"),
    subjectId: yup.string().required("Subject is required").matches(/^\d+$/, "Invalid subject"),
    ...(isEditMode
      ? {
          topicId: yup.string().required("Topic is required").matches(/^\d+$/, "Please select a topic"),
          subTopicId: yup.string().nullable(),
        }
      : {
          topicIds: yup
            .array()
            .of(yup.string())
            .min(1, "Select at least one topic")
            .required("Select at least one topic"),
          subTopicIds: yup.array().of(yup.string()).nullable(),
        }),
    files: fileTests(
      yup.array().when([], {
        is: () => !isEditMode,
        then: (s) => s.min(1, "At least one file is required"),
        otherwise: (s) => s,
      })
    ),
  });

// ─── Component ────────────────────────────────────────────────────────────────

const CourseContent = () => {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEditMode = !!id;
  const isSubtopic = type === "subtopic";

  const [courses, setCourses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subtopicsMap, setSubtopicsMap] = useState({}); // { [topicId]: Topic[] }
  const [loading, setLoading] = useState(false);
  const [fetchingContent, setFetchingContent] = useState(false);
  const [existingFiles, setExistingFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const prevTopicIdsRef = useRef([]);
  const skipSubtopicResetRef = useRef(false);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(createSchema(isEditMode)),
    defaultValues: {
      courseId: "",
      subjectId: "",
      topicId: "",
      subTopicId: "",
      topicIds: [],
      subTopicIds: [],
      files: [],
    },
  });

  const courseId = watch("courseId");
  const subjectId = watch("subjectId");
  const topicId = watch("topicId"); // edit mode
  const topicIds = watch("topicIds") || []; // create mode
  const topicIdsKey = topicIds.join(",");

  // Derived: all subtopics in pool, enriched with parent name for groupBy
  const allSubtopics = Object.entries(subtopicsMap).flatMap(([tid, subs]) => {
    const parentName = topics.find((t) => String(t.id) === tid)?.name || "";
    return (subs || []).map((s) => ({ ...s, _parentName: parentName }));
  });

  // ── Fetch courses ──────────────────────────────────────────────────────────
  useEffect(() => {
    api
      .get("admin/master-form/courses-for-topics")
      .then((res) => setCourses(res.data.data || []))
      .catch(() => toast.error("Failed to fetch courses"));
  }, []);

  // ── Seed courseId from query param ─────────────────────────────────────────
  useEffect(() => {
    const fromQuery = searchParams.get("courseId");
    if (!isEditMode && fromQuery) setValue("courseId", fromQuery);
  }, [searchParams, isEditMode, setValue]);

  // ── Fetch subjects when courseId changes ───────────────────────────────────
  useEffect(() => {
    if (!courseId) {
      setSubjects([]);
      return;
    }
    api
      .get(`admin/master-form/courses/${courseId}/subjects`)
      .then((res) => setSubjects(res.data.data || []))
      .catch(() => toast.error("Failed to fetch subjects"));
  }, [courseId]);

  // ── Fetch topics when course + subject selected ────────────────────────────
  useEffect(() => {
    if (!courseId || !subjectId) {
      setTopics([]);
      if (!isEditMode) {
        setSubtopicsMap({});
        setValue("topicIds", []);
        setValue("subTopicIds", []);
      }
      return;
    }
    api
      .get(`admin/master-form/courses/${courseId}/topics/${subjectId}`)
      .then((res) => {
        const list = res.data.data || [];
        setTopics(Array.isArray(list) ? list : []);
        if (!isEditMode) {
          setSubtopicsMap({});
          setValue("topicIds", []);
          setValue("subTopicIds", []);
        }
      })
      .catch(() => {
        toast.error("Failed to fetch topics");
        setTopics([]);
      });
  }, [courseId, subjectId, isEditMode, setValue]);

  // ── Fetch subtopics for multi-select topics (create mode) ─────────────────
  useEffect(() => {
    if (isEditMode) return;

    const prevIds = prevTopicIdsRef.current;
    const added = topicIds.filter((id) => !prevIds.includes(id));
    const removed = prevIds.filter((id) => !topicIds.includes(id));
    prevTopicIdsRef.current = topicIds;

    if (removed.length > 0) {
      setSubtopicsMap((prev) => {
        const removedSubIds = new Set(
          removed.flatMap((rid) => (prev[rid] || []).map((s) => String(s.id)))
        );
        const currSubs = getValues("subTopicIds") || [];
        const validSubs = currSubs.filter((sid) => !removedSubIds.has(sid));
        if (validSubs.length !== currSubs.length) setValue("subTopicIds", validSubs);
        const next = { ...prev };
        removed.forEach((rid) => delete next[rid]);
        return next;
      });
    }

    added.forEach((tid) => {
      api
        .get(`admin/fetch/topics/subtopics/${tid}`)
        .then((res) => {
          const list = res.data.data || [];
          setSubtopicsMap((prev) => ({ ...prev, [tid]: Array.isArray(list) ? list : [] }));
        })
        .catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicIdsKey, isEditMode]);

  // ── Fetch subtopics for single-select topic (edit mode) ───────────────────
  useEffect(() => {
    if (!isEditMode) return;
    if (!topicId) {
      setSubtopicsMap({});
      return;
    }
    api
      .get(`admin/fetch/topics/subtopics/${topicId}`)
      .then((res) => {
        const list = res.data.data || [];
        setSubtopicsMap({ [topicId]: Array.isArray(list) ? list : [] });
        // Only reset when user changed topic, not during initial load
        if (!skipSubtopicResetRef.current) {
          setValue("subTopicId", "");
        }
        skipSubtopicResetRef.current = false;
      })
      .catch(() => setSubtopicsMap({}));
  }, [topicId, isEditMode, setValue]);

  // ── Load existing content in edit mode ────────────────────────────────────
  useEffect(() => {
    if (!isEditMode || !id) return;

    const load = async () => {
      setFetchingContent(true);
      try {
        const endpoint = isSubtopic
          ? `admin/assign/topic/subtopic/subtopic/${id}`
          : `admin/assign/topic/subtopic/${id}`;

        const { data } = await api.get(endpoint);
        if (!data.success) return;

        const d = data.data;
        const sid = d.subject_id;
        const masterTopicId = d.topic_id || null;
        let resolvedCourseId = d.course_id ? String(d.course_id) : null;

        // 1. Fetch subjects
        if (resolvedCourseId) {
          try {
            const res = await api.get(`admin/master-form/courses/${resolvedCourseId}/subjects`);
            setSubjects(res.data.data || []);
          } catch (_) {}
        }

        // 2. Fetch topics
        if (resolvedCourseId && sid) {
          try {
            const res = await api.get(`admin/master-form/courses/${resolvedCourseId}/topics/${sid}`);
            setTopics(res.data.data || []);
          } catch (_) {}
        }

        // 3. Fetch subtopics
        const tid = isSubtopic ? masterTopicId || d.course_topic_id : masterTopicId;
        if (tid) {
          try {
            const res = await api.get(`admin/fetch/topics/subtopics/${tid}`);
            setSubtopicsMap({ [String(tid)]: res.data.data || [] });
          } catch (_) {}
        }

        // 4. Fallback: no course_id — find a matching course by iterating
        if (!resolvedCourseId && sid) {
          try {
            const allCourses = (await api.get("admin/master-form/courses-for-topics")).data.data || [];
            setCourses(allCourses);
            for (const c of allCourses) {
              try {
                const res = await api.get(`admin/master-form/courses/${c.id}/topics/${sid}`);
                const list = res.data.data || [];
                if (list.length > 0) {
                  resolvedCourseId = String(c.id);
                  setTopics(list);
                  try {
                    const subRes = await api.get(`admin/master-form/courses/${c.id}/subjects`);
                    setSubjects(subRes.data.data || []);
                  } catch (_) {}
                  break;
                }
              } catch (_) {}
            }
          } catch (_) {}
        }

        // 5. Set existing files
        if (d.content_upload != null) {
          const raw = Array.isArray(d.content_upload) ? d.content_upload : [d.content_upload];
          setExistingFiles(
            raw
              .map((item) => (typeof item === "string" ? item : (item?.url ?? item?.path ?? item?.file_url ?? "")))
              .filter(Boolean)
          );
        }

        // 6. Set all form values after all data is ready (so dropdowns have options)
        // Mark initial load so the subtopic useEffect doesn't reset the selection
        skipSubtopicResetRef.current = true;
        if (resolvedCourseId) setValue("courseId", resolvedCourseId);
        if (sid) setValue("subjectId", String(sid));
        if (isSubtopic) {
          if (d.topic_id) setValue("topicId", String(d.topic_id));
          if (d.master_subtopic_id) setValue("subTopicId", String(d.master_subtopic_id));
        } else {
          if (masterTopicId) setValue("topicId", String(masterTopicId));
        }
      } catch (err) {
        if (err.response?.status === 404) {
          toast.error(isSubtopic ? "Subtopic not found" : "Topic not found");
          navigate("/admin/topic/subtopic-list");
        } else {
          toast.error("Failed to load content data");
        }
      } finally {
        setFetchingContent(false);
      }
    };

    load();
  }, [id, isEditMode, isSubtopic, setValue, navigate]);

  // ── File handlers ─────────────────────────────────────────────────────────

  const handleFileUpload = (event) => {
    const newFiles = Array.from(event.target.files);
    const current = watch("files") || [];
    setValue("files", [...current, ...newFiles]);
  };

  const handleFileDelete = (idx) => {
    const current = watch("files") || [];
    setValue("files", current.filter((_, i) => i !== idx));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const dropped = Array.from(e.dataTransfer.files);
    const allowed = dropped.filter(
      (f) => f.type.startsWith("video/") || f.type === "application/pdf" || f.type.startsWith("image/")
    );
    if (allowed.length < dropped.length) toast.warning("Some files were skipped — unsupported format");
    const current = watch("files") || [];
    setValue("files", [...current, ...allowed].slice(0, 10));
  };

  // ── Submit ─────────────────────────────────────────────────────────────────

  const onSubmit = async (formData) => {
    setLoading(true);
    const data = new FormData();
    data.append("subject_id", formData.subjectId);

    if (isEditMode) {
      const topicName = topics.find((t) => String(t.id) === String(formData.topicId))?.name || "";
      const subtopicName = allSubtopics.find((s) => String(s.id) === String(formData.subTopicId))?.name || "";
      data.append("topic_name", topicName);
      if (subtopicName) data.append("subtopic_name", subtopicName);
      if (formData.courseId) data.append("course_id", formData.courseId);
    } else {
      data.append("course_id", formData.courseId);
      (formData.topicIds || []).forEach((tid) => data.append("topic_ids[]", tid));
      (formData.subTopicIds || []).forEach((sid) => data.append("subtopic_ids[]", sid));
    }

    if (formData.files?.length > 0) {
      formData.files.forEach((f) => data.append("content_upload[]", f));
    }

    try {
      if (isEditMode) {
        const ep = isSubtopic
          ? `admin/assign/topic/subtopic/subtopic/${id}`
          : `admin/assign/topic/subtopic/${id}`;
        await api.put(ep, data);
        toast.success("Content updated successfully!");
      } else {
        await api.post("admin/assign/topic/subtopic", data);
        toast.success("Content uploaded successfully!");
        reset();
        prevTopicIdsRef.current = [];
        setSubtopicsMap({});
      }
      navigate("/admin/topic/subtopic-list");
    } catch (error) {
      if (error.response?.status === 422) {
        const errs = error.response.data.errors;
        if (errs) {
          Object.entries(errs).forEach(([field, messages]) => {
            toast.error(`${field}: ${Array.isArray(messages) ? messages[0] : messages}`);
          });
        } else {
          toast.error(error.response.data?.message || "Validation error occurred");
        }
      } else if (error.response?.status === 404) {
        toast.error(isSubtopic ? "Subtopic not found" : "Topic not found");
        if (isEditMode) navigate("/admin/topic/subtopic-list");
      } else if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        localStorage.removeItem("token");
        window.location.href = "/login";
      } else {
        toast.error(isEditMode ? "Failed to update content" : "Failed to upload content");
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Loading skeleton ───────────────────────────────────────────────────────

  if (fetchingContent) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress size={56} sx={{ color: "#667eea" }} />
          <Typography variant="h6" sx={{ mt: 2, color: "#5a6c7d" }}>
            Loading content data…
          </Typography>
        </Box>
      </Box>
    );
  }

  const topicCount = topicIds.length;
  const submitLabel = loading
    ? isEditMode
      ? "Updating…"
      : "Uploading…"
    : isEditMode
    ? "Update Content"
    : topicCount > 1
    ? `Upload to ${topicCount} topics`
    : "Upload Content";

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        p: 3,
      }}
    >
      {/* Page header */}
      <Box sx={{ mb: 4, display: "flex", alignItems: "center", gap: 2 }}>
        <Avatar
          sx={{
            width: 48,
            height: 48,
            background: gradientBg,
            boxShadow: "0 4px 15px rgba(102,126,234,0.35)",
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 26 }} />
        </Avatar>
        <Box>
          <Typography variant="h5" fontWeight={700} color="#1a1a2e" lineHeight={1.2}>
            {isEditMode
              ? isSubtopic
                ? "Edit Subtopic Content"
                : "Edit Topic Content"
              : "Upload Content"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {isEditMode
              ? "Update videos, PDFs, and images for this topic"
              : "Upload videos, PDFs, and images for course topics and subtopics"}
          </Typography>
        </Box>
      </Box>

      <Card
        sx={{
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
          border: "1px solid rgba(255,255,255,0.8)",
          background: "rgba(255,255,255,0.97)",
          backdropFilter: "blur(20px)",
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>

              {/* ── Section 1: Classification ─────────────────────────── */}
              <Grid item xs={12}>
                <SectionHeader number="1" label="Classification" />
              </Grid>

              {/* Course */}
              <Grid item xs={12} sm={6}>
                <Box sx={fieldBoxSx}>
                  <Typography variant="caption" fontWeight={600} color="#5a6c7d" sx={{ mb: 1, display: "block", textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Course
                  </Typography>
                  <Controller
                    name="courseId"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.courseId}>
                        <Select {...field} displayEmpty sx={selectSx}>
                          <MenuItem value="" disabled>Select Course</MenuItem>
                          {courses.map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                              {c.name || `Course ${c.id}`}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.courseId && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                            {errors.courseId.message}
                          </Typography>
                        )}
                      </FormControl>
                    )}
                  />
                </Box>
              </Grid>

              {/* Subject */}
              <Grid item xs={12} sm={6}>
                <Box sx={fieldBoxSx}>
                  <Typography variant="caption" fontWeight={600} color="#5a6c7d" sx={{ mb: 1, display: "block", textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Subject
                  </Typography>
                  <Controller
                    name="subjectId"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.subjectId}>
                        <Select {...field} displayEmpty sx={selectSx}>
                          <MenuItem value="" disabled>Select Subject</MenuItem>
                          {subjects.map((s) => (
                            <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                          ))}
                        </Select>
                        {errors.subjectId && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                            {errors.subjectId.message}
                          </Typography>
                        )}
                      </FormControl>
                    )}
                  />
                </Box>
              </Grid>

              {/* ── Section 2: Topics ─────────────────────────────────── */}
              <Grid item xs={12}>
                <SectionHeader
                  number="2"
                  label={isEditMode ? "Topic" : "Topics (select one or more)"}
                />
                <Box sx={fieldBoxSx}>
                  {isEditMode ? (
                    /* Edit mode — single Select */
                    <Controller
                      name="topicId"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.topicId}>
                          <Select
                            {...field}
                            displayEmpty
                            disabled={!courseId || !subjectId}
                            sx={selectSx}
                          >
                            <MenuItem value="" disabled>
                              {!courseId
                                ? "Select Course first"
                                : !subjectId
                                ? "Select Subject first"
                                : topics.length === 0
                                ? "No topics available"
                                : "Select Topic"}
                            </MenuItem>
                            {topics.map((t) => (
                              <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>
                            ))}
                          </Select>
                          {errors.topicId && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                              {errors.topicId.message}
                            </Typography>
                          )}
                        </FormControl>
                      )}
                    />
                  ) : (
                    /* Create mode — multi-select Autocomplete */
                    <Controller
                      name="topicIds"
                      control={control}
                      render={({ field }) => (
                        <>
                          <Autocomplete
                            multiple
                            options={topics}
                            value={topics.filter((t) => (field.value || []).includes(String(t.id)))}
                            onChange={(_, newVal) => field.onChange(newVal.map((t) => String(t.id)))}
                            getOptionLabel={(t) => t.name}
                            isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                            disabled={!courseId || !subjectId}
                            noOptionsText={
                              !courseId
                                ? "Select Course first"
                                : !subjectId
                                ? "Select Subject first"
                                : "No topics available"
                            }
                            renderTags={(vals, getTagProps) =>
                              vals.map((t, i) => (
                                <Chip
                                  label={t.name}
                                  {...getTagProps({ index: i })}
                                  size="small"
                                  sx={{
                                    background: gradientBg,
                                    color: "white",
                                    fontWeight: 500,
                                    "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.7)", "&:hover": { color: "white" } },
                                  }}
                                />
                              ))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder={
                                  (field.value || []).length === 0
                                    ? !courseId
                                      ? "Select Course first"
                                      : !subjectId
                                      ? "Select Subject first"
                                      : "Search and select topics…"
                                    : ""
                                }
                              />
                            )}
                            sx={autocompleteSx}
                          />
                          {errors.topicIds && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                              {errors.topicIds.message}
                            </Typography>
                          )}
                        </>
                      )}
                    />
                  )}
                </Box>
              </Grid>

              {/* ── Section 3: Sub Topics ─────────────────────────────── */}
              <Grid item xs={12}>
                <SectionHeader
                  number="3"
                  label={isEditMode ? "Sub Topic (Optional)" : "Sub Topics (optional, select one or more)"}
                />
                <Box sx={fieldBoxSx}>
                  {isEditMode ? (
                    /* Edit mode — single Select */
                    <Controller
                      name="subTopicId"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <Select
                            {...field}
                            displayEmpty
                            disabled={!topicId}
                            sx={selectSx}
                          >
                            <MenuItem value="">
                              {!topicId
                                ? "Select Topic first"
                                : allSubtopics.length === 0
                                ? "No subtopics available"
                                : "None (topic-level content)"}
                            </MenuItem>
                            {allSubtopics.map((s) => (
                              <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  ) : (
                    /* Create mode — multi-select Autocomplete grouped by parent topic */
                    <Controller
                      name="subTopicIds"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          multiple
                          options={allSubtopics}
                          value={allSubtopics.filter((s) => (field.value || []).includes(String(s.id)))}
                          onChange={(_, newVal) => field.onChange(newVal.map((s) => String(s.id)))}
                          getOptionLabel={(s) => s.name}
                          isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                          groupBy={(s) => s._parentName}
                          disabled={topicIds.length === 0}
                          noOptionsText={
                            topicIds.length === 0
                              ? "Select topics first"
                              : "No subtopics available for selected topics"
                          }
                          renderTags={(vals, getTagProps) =>
                            vals.map((s, i) => (
                              <Chip
                                label={s.name}
                                {...getTagProps({ index: i })}
                                size="small"
                                sx={{
                                  background: "linear-gradient(135deg, #a78bfa, #7c3aed)",
                                  color: "white",
                                  fontWeight: 500,
                                  "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.7)", "&:hover": { color: "white" } },
                                }}
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={
                                (field.value || []).length === 0
                                  ? topicIds.length === 0
                                    ? "Select topics first"
                                    : "Search and select subtopics…"
                                  : ""
                              }
                            />
                          )}
                          sx={autocompleteSx}
                        />
                      )}
                    />
                  )}
                </Box>
              </Grid>

              {/* ── Existing Files (edit mode) ────────────────────────── */}
              {isEditMode && existingFiles.length > 0 && (
                <Grid item xs={12}>
                  <SectionHeader number="4" label={`Current Files (${existingFiles.length})`} />
                  <Box sx={{ ...fieldBoxSx, background: "linear-gradient(135deg, #e3f2fd, #bbdefb)" }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                      Uploading new files will replace all existing files
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, maxHeight: 280, overflowY: "auto" }}>
                      {existingFiles.map((fileUrl, idx) => {
                        if (!fileUrl || typeof fileUrl !== "string") return null;
                        const fileName = fileUrl.split("/").pop() || `File ${idx + 1}`;
                        const isVideo = /\.(mp4|mov|avi|wmv)$/i.test(fileUrl);
                        const isPdf = /\.pdf$/i.test(fileUrl);
                        const isImage = /\.(jpg|jpeg|png)$/i.test(fileUrl);
                        return (
                          <Box
                            key={idx}
                            sx={{ display: "flex", alignItems: "center", gap: 1, bgcolor: "white", borderRadius: 2, px: 1.5, py: 1, boxShadow: "0 2px 8px rgba(0,0,0,0.08)", minWidth: 160, maxWidth: 240 }}
                          >
                            {isVideo && <VideoLibraryIcon color="primary" fontSize="small" />}
                            {isPdf && <PictureAsPdfIcon color="error" fontSize="small" />}
                            {isImage && <ImageIcon color="success" fontSize="small" />}
                            {!isVideo && !isPdf && !isImage && <AttachFileIcon fontSize="small" />}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={500} noWrap sx={{ fontSize: 12 }}>{fileName}</Typography>
                              <Link href={fileUrl} target="_blank" rel="noopener noreferrer" sx={{ fontSize: 11, textDecoration: "none" }}>
                                View
                              </Link>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Grid>
              )}

              {/* ── Section: File Upload ──────────────────────────────── */}
              <Grid item xs={12}>
                <SectionHeader
                  number={isEditMode && existingFiles.length > 0 ? "5" : "4"}
                  label="Files"
                />

                <Box
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  sx={{
                    borderRadius: 2,
                    border: isDragOver
                      ? "2px dashed #7c3aed"
                      : "2px dashed rgba(102,126,234,0.35)",
                    background: isDragOver
                      ? "linear-gradient(135deg, #ede9fe, #ddd6fe)"
                      : "white",
                    boxShadow: isDragOver ? "0 0 0 4px rgba(124,58,237,0.1)" : "0 2px 12px rgba(0,0,0,0.04)",
                    transition: "all 0.2s ease",
                    p: 4,
                    textAlign: "center",
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        background: isDragOver ? "linear-gradient(135deg, #7c3aed, #6d28d9)" : gradientBg,
                        transition: "all 0.2s ease",
                        animation: isDragOver ? "bounce 0.6s infinite alternate" : "none",
                        "@keyframes bounce": {
                          from: { transform: "translateY(0)" },
                          to: { transform: "translateY(-8px)" },
                        },
                      }}
                    >
                      <CloudUploadIcon sx={{ fontSize: 32 }} />
                    </Avatar>

                    {isDragOver ? (
                      <Typography variant="h6" fontWeight={700} color="#7c3aed">
                        Release to upload
                      </Typography>
                    ) : (
                      <>
                        <Typography variant="h6" fontWeight={700} color="#1a1a2e">
                          {isEditMode ? "Drag & Drop new files here" : "Drag & Drop files here"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          MP4 · MOV · AVI · WMV · PDF · JPG · PNG &nbsp;·&nbsp; Max 50 MB per file · 10 files max
                        </Typography>
                        {isEditMode && existingFiles.length > 0 && (
                          <Typography variant="caption" color="warning.main" fontWeight={600}>
                            ⚠️ Uploading new files will replace all existing files
                          </Typography>
                        )}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%", maxWidth: 280, my: 0.5 }}>
                          <Box sx={{ flex: 1, height: "1px", bgcolor: "rgba(0,0,0,0.1)" }} />
                          <Typography variant="caption" color="text.disabled" fontWeight={600}>or</Typography>
                          <Box sx={{ flex: 1, height: "1px", bgcolor: "rgba(0,0,0,0.1)" }} />
                        </Box>
                        <Button
                          variant="contained"
                          component="label"
                          sx={gradientButtonSx}
                          startIcon={<AttachFileIcon />}
                        >
                          Choose Files
                          <input
                            type="file"
                            hidden
                            multiple
                            accept="video/*,application/pdf,image/*"
                            onChange={handleFileUpload}
                          />
                        </Button>
                      </>
                    )}

                    {/* Selected files preview */}
                    <Controller
                      name="files"
                      control={control}
                      render={({ field }) =>
                        field.value && field.value.length > 0 ? (
                          <Box sx={{ mt: 2, width: "100%" }}>
                            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, textAlign: "left" }}>
                              Selected Files ({field.value.length})
                            </Typography>
                            <Grid container spacing={1.5}>
                              {field.value.map((file, idx) => (
                                <Grid item xs={12} sm={6} md={4} key={idx}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1.5,
                                      bgcolor: "white",
                                      border: "1px solid rgba(102,126,234,0.12)",
                                      borderRadius: 2,
                                      p: 1.5,
                                      position: "relative",
                                      "&:hover .del-btn": { opacity: 1 },
                                    }}
                                  >
                                    <Avatar
                                      sx={{
                                        width: 36,
                                        height: 36,
                                        flexShrink: 0,
                                        background: file.type?.startsWith("video/")
                                          ? "linear-gradient(135deg, #2196f3, #1976d2)"
                                          : file.type === "application/pdf"
                                          ? "linear-gradient(135deg, #f44336, #d32f2f)"
                                          : "linear-gradient(135deg, #4caf50, #2e7d32)",
                                      }}
                                    >
                                      {file.type?.startsWith("video/") && <VideoLibraryIcon sx={{ fontSize: 17 }} />}
                                      {file.type === "application/pdf" && <PictureAsPdfIcon sx={{ fontSize: 17 }} />}
                                      {file.type?.startsWith("image/") && <ImageIcon sx={{ fontSize: 17 }} />}
                                      {!file.type?.startsWith("video/") && file.type !== "application/pdf" && !file.type?.startsWith("image/") && <AttachFileIcon sx={{ fontSize: 17 }} />}
                                    </Avatar>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: 12 }}>{file.name}</Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {(file.size / (1024 * 1024)).toFixed(2)} MB
                                      </Typography>
                                    </Box>
                                    <Box
                                      className="del-btn"
                                      sx={{ opacity: 0, transition: "opacity 0.2s", flexShrink: 0 }}
                                    >
                                      <Button
                                        size="small"
                                        onClick={() => handleFileDelete(idx)}
                                        sx={{
                                          minWidth: "auto",
                                          width: 26,
                                          height: 26,
                                          borderRadius: "50%",
                                          background: "linear-gradient(135deg, #f44336, #d32f2f)",
                                          color: "white",
                                          p: 0,
                                        }}
                                      >
                                        <DeleteIcon sx={{ fontSize: 13 }} />
                                      </Button>
                                    </Box>
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        ) : null
                      }
                    />

                    {errors.files && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                        {errors.files.message}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grid>

              {/* ── Submit ────────────────────────────────────────────── */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", mt: 1 }}>
                  <Button
                    type="submit"
                    size="large"
                    disabled={loading || fetchingContent}
                    sx={{ ...gradientButtonSx, px: 5, py: 1.75, fontSize: 15, minWidth: 200 }}
                    startIcon={
                      loading ? (
                        <CircularProgress size={18} sx={{ color: "white" }} />
                      ) : (
                        <CloudUploadIcon />
                      )
                    }
                    endIcon={!loading && <ArrowForwardIcon />}
                  >
                    {submitLabel}
                  </Button>
                </Box>
              </Grid>

            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CourseContent;
